/**
 * D1 funnel_events sink — buffers analytics events from EventBus and flushes
 * them to the Worker beacon endpoint (POST /api/funnel_events) for D1 storage.
 *
 * Per FUNNEL_ENGINE_FOUNDATION §3.2/§3.3 + Wave 1 W1-B1 beacon contract:
 *   - Batch up to 25 events per flush (sendBeacon 64 KiB hard limit at
 *     realistic 1-2 KB per event).
 *   - 2-second flush timer auto-fires for inactive sessions (otherwise
 *     events would sit in buffer until session ends).
 *   - Content-Type: text/plain (sendBeacon avoids CORS preflight).
 *   - sendBeacon preferred; falls back to fetch(keepalive=true) when the
 *     browser doesn't support sendBeacon OR when the body exceeds the
 *     browser's per-call sendBeacon limit (~64 KiB).
 *   - Failure surfacing: when sendBeacon returns false OR fetch fails,
 *     fire-and-forget POST to /api/_health/beacon_fail counter so the
 *     Worker's beacon-fail-alert scheduled handler (W1-B5) can detect
 *     elevated failure rates.
 *
 * This sink is ADDITIVE — it does not replace GA4 / Clarity / Meta Pixel.
 * EventBus continues to fan each event to GA4 + Clarity + Pixel + (now) D1.
 *
 * Per F-E12 (R2 fix): field_value_hash is computed SERVER-SIDE by the Worker
 * (sha256 of field_value). The browser sends raw field_value here; the Worker
 * hashes it before D1 write. We DO NOT hash on the browser side.
 */

import type { FunnelEventName, EventProps } from "./eventBus";

/** Shape of one event sent to /api/funnel_events. Mirrors the Worker handler's
 *  IncomingEvent interface (app/lead_distribution/internal_api/src/funnel_events.ts).
 *  Fields prefixed with ? are optional and only populated when relevant.
 */
export interface FunnelEventPayload {
  event_name: string;
  funnel_id: string;
  funnel_variant_id: string;
  session_id: string;
  visitor_id: string;
  timestamp: string;   // ISO 8601 UTC, browser-side

  // UTM dimensions
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  meta_campaign_id?: string;
  meta_adset_id?: string;
  meta_ad_id?: string;
  meta_placement?: string;

  // Dynamic content selectors (Worker serializes {angle,h,o,p} → content_selector_json)
  angle?: string;
  h?: string;
  o?: string;
  p?: string;

  // Step/field context
  step_id?: string;
  step_index?: number;
  step_total?: number;
  field_id?: string;
  field_value?: string;       // Worker hashes server-side before D1 write
  field_value_length?: number;
  validation_error_code?: string;

  // A/B
  ab_test_id?: string;
  ab_variant_id?: string;

  // Branching
  branch_from_step?: string;
  branch_to_step?: string;
  branch_condition?: string;

  // Submission
  lead_correlation_id?: string;
  submit_latency_ms?: number;
  submit_error_code?: string;

  // Device
  device_type?: string;
  browser?: string;
  os?: string;
  viewport_width?: number;
  viewport_height?: number;

  // Escape hatch
  extra_json?: string;
}

/** Static-for-the-session context that every event in the buffer carries. */
export interface D1SinkContext {
  funnel_id: string;
  funnel_variant_id: string;
  session_id: string;
  visitor_id: string;

  // Captured once at sink construction time (URL params + UA + viewport).
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  meta_campaign_id?: string;
  meta_adset_id?: string;
  meta_ad_id?: string;
  meta_placement?: string;
  angle?: string;
  h?: string;
  o?: string;
  p?: string;
  device_type?: string;
  browser?: string;
  os?: string;
  viewport_width?: number;
  viewport_height?: number;
}

const MAX_BATCH = 25;
const FLUSH_INTERVAL_MS = 2000;

export class D1Sink {
  private readonly endpoint: string;
  private readonly failCounterEndpoint: string;
  private readonly context: D1SinkContext;
  private buffer: FunnelEventPayload[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * @param beaconEndpoint   absolute URL to POST funnel_events to,
   *                         e.g. "https://api.truecasenetwork.com/api/funnel_events"
   * @param failCounterEndpoint absolute URL of the /api/_health/beacon_fail counter,
   *                            e.g. "https://api.truecasenetwork.com/api/_health/beacon_fail"
   * @param context  per-session context every event in buffer carries
   */
  constructor(beaconEndpoint: string, failCounterEndpoint: string, context: D1SinkContext) {
    this.endpoint = beaconEndpoint;
    this.failCounterEndpoint = failCounterEndpoint;
    this.context = context;

    // Flush on tab close (page-hide / before-unload). sendBeacon is the right
    // tool for this — survives the tab going away.
    if (typeof window !== "undefined") {
      const flushOnExit = (): void => {
        this.flush();
      };
      window.addEventListener("pagehide", flushOnExit);
      window.addEventListener("beforeunload", flushOnExit);
    }
  }

  /** Add one event to the buffer. Auto-flushes when buffer hits MAX_BATCH. */
  push(name: FunnelEventName | string, props: EventProps = {}): void {
    const payload: FunnelEventPayload = {
      event_name: String(name),
      funnel_id: this.context.funnel_id,
      funnel_variant_id: this.context.funnel_variant_id,
      session_id: this.context.session_id,
      visitor_id: this.context.visitor_id,
      timestamp: new Date().toISOString(),

      // Spread session-static context first; props can override (rare).
      utm_source: this.context.utm_source,
      utm_medium: this.context.utm_medium,
      utm_campaign: this.context.utm_campaign,
      utm_content: this.context.utm_content,
      utm_term: this.context.utm_term,
      meta_campaign_id: this.context.meta_campaign_id,
      meta_adset_id: this.context.meta_adset_id,
      meta_ad_id: this.context.meta_ad_id,
      meta_placement: this.context.meta_placement,
      angle: this.context.angle,
      h: this.context.h,
      o: this.context.o,
      p: this.context.p,
      device_type: this.context.device_type,
      browser: this.context.browser,
      os: this.context.os,
      viewport_width: this.context.viewport_width,
      viewport_height: this.context.viewport_height,
    };

    // Per-event props (step_id, field_id, etc.). Filter to allowed keys
    // so a careless caller can't blow up the Worker handler's schema.
    const allowedKeys: Set<keyof FunnelEventPayload> = new Set([
      "step_id",
      "step_index",
      "step_total",
      "field_id",
      "field_value",
      "field_value_length",
      "validation_error_code",
      "ab_test_id",
      "ab_variant_id",
      "branch_from_step",
      "branch_to_step",
      "branch_condition",
      "lead_correlation_id",
      "submit_latency_ms",
      "submit_error_code",
      "extra_json",
    ]);
    for (const [key, value] of Object.entries(props)) {
      if (value === undefined || value === null) continue;
      if (!allowedKeys.has(key as keyof FunnelEventPayload)) continue;
      // String / number / boolean — Worker accepts these. We coerce boolean to string for D1 TEXT cols.
      const k = key as keyof FunnelEventPayload;
      // We use a typed assignment via any to avoid the union-distribution issue.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (payload as any)[k] = typeof value === "boolean" ? String(value) : value;
    }

    this.buffer.push(payload);

    if (this.buffer.length >= MAX_BATCH) {
      this.flush();
    } else {
      this.scheduleFlush();
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = setTimeout(() => {
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  /** Drain the buffer to /api/funnel_events. Idempotent + safe to call repeatedly. */
  flush(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.buffer.length === 0) return;

    const batch = this.buffer.slice(0, MAX_BATCH);
    this.buffer = this.buffer.slice(MAX_BATCH);

    const body = JSON.stringify({ events: batch });

    // Prefer navigator.sendBeacon — survives tab close.
    const blob = new Blob([body], { type: "text/plain" });
    let delivered = false;
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        delivered = navigator.sendBeacon(this.endpoint, blob);
      } catch {
        delivered = false;
      }
    }

    if (!delivered) {
      // Fallback to fetch with keepalive (less-reliable on tab close but works in modern browsers).
      try {
        void fetch(this.endpoint, {
          method: "POST",
          body,
          headers: { "content-type": "text/plain" },
          keepalive: true,
        }).then((res) => {
          if (!res.ok) this.pingFailCounter(batch.length);
        }).catch(() => {
          this.pingFailCounter(batch.length);
        });
      } catch {
        this.pingFailCounter(batch.length);
      }
    }

    // If there are still events in the buffer (overflow > MAX_BATCH), recurse.
    if (this.buffer.length > 0) this.flush();
  }

  /** Fire-and-forget ping to the beacon-fail counter Worker endpoint (W1-B5). */
  private pingFailCounter(eventCount: number): void {
    try {
      const body = JSON.stringify({ event_count: eventCount });
      const blob = new Blob([body], { type: "text/plain" });
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(this.failCounterEndpoint, blob);
      } else {
        // Best-effort fetch; explicitly ignore errors (this IS the failure path).
        void fetch(this.failCounterEndpoint, {
          method: "POST",
          body,
          headers: { "content-type": "text/plain" },
          keepalive: true,
        }).catch(() => undefined);
      }
    } catch {
      // No-op — this is the failure path of the failure path. Logging would
      // require an even-more-failure-path. Accept silent loss here.
    }
  }
}
